import {
  Action,
  ActionSubject,
  OperationalAction,
  OperationalSubject,
  type FeatureEvents,
  type FeatureEventUi,
  type OperationalEvent,
  type ScreenEvent,
  type TrackEvent,
  type UIEvent,
} from '../new-analytics-types';

type ScreenKeys = 'jsmGracePeriodBannerScreenEvent';

type UiEventKeys = 'tryPremiumForFreeButtonClick' | 'upgradeNowButtonClick' | 'readMoreAboutTheUpdatesLinkClick';

// Screens
export const jsmGracePeriodBannerScreen = 'jsmGracePeriodBannerScreen';

export const withMigrationAttribute = (fn) => (attributes) => {
  const newAttributes = { ...attributes, migration: true };

  return fn(newAttributes);
};

export type FeatureEventsScreenType = Record<ScreenKeys, (attributes?: Record<string, any>) => ScreenEvent>;
export type FeatureEventsUIType = Record<UiEventKeys, FeatureEventUi>;
export type FeatureEventsTrackType = Record<string, (attributes?: Record<string, any>) => TrackEvent>;
export type FeatureEventsOperationalType = Record<string, (attributes?: Record<string, any>) => OperationalEvent>;

export const mapEventsWithMigrationAttribute = <T extends Record<string, (attributes: Record<string, any>) => any>>(events: T): T =>
  Object.fromEntries(Object.entries(events).map(([key, eventFn]) => [key, withMigrationAttribute(eventFn)])) as T;

export const jsmGracePeriodBannerEvents: FeatureEvents<ScreenKeys, UiEventKeys, string, string> = {
  confluenceSpec: 'https://hello.atlassian.net/wiki/spaces/ITSOL/pages/5627616466/AdminHub+-+Analytic+Events',
  screen: mapEventsWithMigrationAttribute<FeatureEventsScreenType>({
    jsmGracePeriodBannerScreenEvent: (attributes): ScreenEvent => ({
      data: {
        name: jsmGracePeriodBannerScreen,
        attributes,
      },
      dataPortalLink: 'http://data-portal.internal.atlassian.com/analytics/registry/86694',
    }),
  }),
  ui: mapEventsWithMigrationAttribute<FeatureEventsUIType>({
    tryPremiumForFreeButtonClick: (attributes: { cloudId: string; page: string }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'tryPremiumForFreeButton',
        source: jsmGracePeriodBannerScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/86691',
    }),
    upgradeNowButtonClick: (attributes: { cloudId: string; page: string }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'upgradeNowButton',
        source: jsmGracePeriodBannerScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/86692',
    }),
    readMoreAboutTheUpdatesLinkClick: (attributes: { cloudId: string; page: string }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Link,
        actionSubjectId: 'readMoreAboutTheUpdatesLink',
        source: jsmGracePeriodBannerScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/86693',
    }),
  }),
  track: mapEventsWithMigrationAttribute<FeatureEventsTrackType>({}),
  operational: mapEventsWithMigrationAttribute<FeatureEventsOperationalType>({
    fetchGpEndDataFailed: (attributes: { cloudId: string; page: string; error: string }): OperationalEvent => ({
      data: {
        action: OperationalAction.Failed,
        actionSubject: OperationalSubject.JsmGpEndDataFetch,
        source: jsmGracePeriodBannerScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/86698',
    }),
  }),
};
